import React, { PropsWithChildren } from 'react';
import ProductItem from '../../../../components-library/menu/product/ProductItem';
import useAppMethods from '../../../../hooks/utility/useAppMethods';
import GiftCardNameRow from '../GiftCardNameRow';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useGiftCardLimits from '../../hooks/useGiftCardLimits';
import useScreenType from '../../../../hooks/utility/useScreenType';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import GiftCardIcon from '../../../../assets/logo/GiftCardIcon';

const DESCRIPTION_ITEMS: Readonly<string[]> = ['delivery', 'usage', 'value'];

const WithScreenWrapper = ({ children }: PropsWithChildren) => {
  const { isDesktop } = useScreenType();
  const { toggleBuyGiftCardModal } = useAppMethods();
  const toggleProduct = () => {
    toggleBuyGiftCardModal({ openModal: true });
  };

  if (isDesktop) {
    return <ProductItem.Web toggleProduct={toggleProduct}>{children}</ProductItem.Web>;
  }

  return <ProductItem.Mobile toggleProduct={toggleProduct}>{children}</ProductItem.Mobile>;
};

const GiftCardCategoryItem = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:GiftCards:GiftCardCategory' });
  const { formatCurrency } = useAppMethods();
  const { min, max } = useGiftCardLimits();
  const from = formatCurrency(min / 100);
  const to = formatCurrency(max / 100);
  const redemptionMode = useSelector(
    (state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA]?.franchise?.gift_cards_redemption_mode,
  );
  const getTranslation = (key: string) => {
    if (key !== 'usage') {
      return t(`description.items.${key}`, { from, to });
    }

    return t(`description.items.${key}.${redemptionMode}`);
  };

  return (
    <WithScreenWrapper>
      <ProductItem.LeftColumn className="!justify-normal">
        <ProductItem.ProductName name={<GiftCardNameRow />} />
        <div className="space-y-0 md:space-y-xs">
          {DESCRIPTION_ITEMS.map((item) => (
            <DescriptionItem key={item}>{getTranslation(item)}</DescriptionItem>
          ))}
        </div>
      </ProductItem.LeftColumn>
      <ProductItem.ProductImage>
        <div className="flex justify-center size-full bg-brand-primary/75">
          <GiftCardIcon className="w-2/3 text-white p-sm aspect-square object-cover lg:rounded-none rounded-lg" />
        </div>
      </ProductItem.ProductImage>
    </WithScreenWrapper>
  );
};

const DescriptionItem = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex items-center gap-2">
      <div className="h-5 w-5 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
        <Check className="h-3 w-3 text-brand-primary" />
      </div>
      <span className="text-xs md:text-sm text-muted-foreground">{children}</span>
    </div>
  );
};

export default GiftCardCategoryItem;
